import { posthog } from "utils/posthog";

/**
 * Product analytics for the gates between signup and a first reconciliation.
 *
 * Three of those gates emit nothing today. `SignupAttribution` and
 * `OnBoardOverview` render in place from `app/Protected.tsx`, so neither
 * changes the URL and neither produces a `$pageview`; the Add Company wizard
 * advances a zustand step with no event at all. Autocapture records that some
 * button was clicked, which cannot tell "nobody reached this gate" apart from
 * "everybody who reached it gave up on it" — so none of the three can be
 * ranked or trended.
 *
 * 🪤 These capture PostHog directly. `utils/analytics.ts` also reaches PostHog
 * — its `analytics.on("track")` mirror forwards every `trackEvents` name — so
 * the two modules are two paths to the same project, not one working path and
 * one dead one. Pick ONE per event name. An event declared in both arrives
 * twice and nothing errors; the only symptom is a funnel step that reads 2x.
 *
 * `environment` is a posthog-js super-property (registered in
 * `utils/posthog.ts`), so both paths carry the dev/prod discriminator that
 * makes an event queryable at all — dev and production share one PostHog
 * project.
 *
 * Every event name and payload is declared here so the names cannot drift
 * across call sites.
 */
export const ACTIVATION_EVENTS = {
	signupSubmitted: "signup_submitted",
	signupFailed: "signup_failed",
	attributionGateShown: "attribution_gate_shown",
	attributionGateSignedOut: "attribution_gate_signed_out",
	onboardingOverviewShown: "onboarding_overview_shown",
	onboardingOverviewSkipped: "onboarding_overview_skipped",
	onboardingOverviewCompleted: "onboarding_overview_completed",
	verifyEmailGateShown: "verify_email_gate_shown",
	verifyEmailResendClicked: "verify_email_resend_clicked",
	addCompanyStepCompleted: "add_company_step_completed",
	addCompanyAbandoned: "add_company_abandoned",
	trialCompanyLimitBlocked: "trial_company_limit_blocked",
	trialConvertClicked: "trial_convert_clicked",
} as const;

/** How the account was created. */
export type SignupMethod = "password" | "google";

/** Which gesture reached for the disabled Add Company control. */
export type TrialLimitBlockedTrigger = "click" | "hover";

/**
 * Which control on /account/billing sent the user to Stripe Checkout.
 *
 * 🪤 This names the CONTROL, not the campaign that produced the visit. The
 * third value the spec asked for, `lifecycle_email`, is deliberately absent:
 * the T-48h `trial_ending_soon` email still carries a pre-minted Stripe URL
 * (`createTrialConversionCheckoutUrl` in `equility-api`), so that click never
 * loads the app and no frontend event can observe it. Inventing the value here
 * would produce a `source` breakdown that silently reads 0 email conversions
 * when the real answer is "not measured here". Those sessions are
 * distinguishable in Stripe instead, by `metadata.source = trial_ending_soon`.
 */
export type TrialConvertSource = "trial_banner" | "lapsed_resubscribe";

function capture(
	event: string,
	properties: Record<string, unknown> = {},
): void {
	try {
		posthog.capture(event, properties);
	} catch {
		// Analytics must never break a gate. These events sit on the signup,
		// verification and wizard paths, so a capture that throws — a blocked
		// host, a wedged posthog-js, no API key configured — must not stop a
		// user creating an account.
	}
}

/**
 * The error code behind a failed signup.
 *
 * 🪤 Returns a CODE only, never a message. `getErrorMsg` descriptions are
 * human prose that can quote the address back ("Email already in use", API
 * validation text), and this value ships to a third party. Firebase's `code`
 * and the API's coded refusals are closed enums; the HTTP status is the
 * fallback. `error.message` is deliberately never read.
 */
export function signupFailureReason(error: unknown): string {
	const firebaseCode = (error as { code?: unknown } | null)?.code;
	if (typeof firebaseCode === "string" && firebaseCode) return firebaseCode;

	const response = (
		error as {
			response?: {
				status?: number;
				data?: { code?: unknown; message?: { code?: unknown } };
			};
		} | null
	)?.response;
	const apiCode = response?.data?.message?.code ?? response?.data?.code;
	if (typeof apiCode === "string" && apiCode) return apiCode;
	if (typeof response?.status === "number") return `http_${response.status}`;

	return "unknown";
}

export function trackSignupSubmitted(method: SignupMethod): void {
	capture(ACTIVATION_EVENTS.signupSubmitted, { method });
}

export function trackSignupFailed(method: SignupMethod, error: unknown): void {
	capture(ACTIVATION_EVENTS.signupFailed, {
		method,
		reason: signupFailureReason(error),
	});
}

/**
 * 🪤 `account_created` is deliberately NOT emitted here. Do not add it back.
 *
 * It is emitted server-side, once, by `AuthService.captureAccountCreated` in
 * equility-api (EQU-110). This file used to emit it too (EQU-216) and for a
 * while both fired: one account produced two events 6ms apart, which inflated
 * the activation denominator the event exists to deflate.
 *
 * The server sink is the one to keep, not just the one that won:
 * - it reads `firebase.sign_in_provider` off the VERIFIED token, where this
 *   file could only pass a caller-supplied "password" | "google" flag;
 * - it resolves `team_invite`, which wins over the auth provider. An invited
 *   teammate who signs up with Google is not a Google acquisition — the
 *   distinction behind the 47 -> 32 Stripe reconciliation gap. This file
 *   reported that person as `method: "google"`;
 * - it cannot be dropped by an ad blocker, a closed tab, or `navigator.webdriver`.
 *
 * Note the property name differs: the server event carries `signup_method`
 * (with a `team_invite` value), never `method`.
 */

export function trackAttributionGateShown(): void {
	capture(ACTIVATION_EVENTS.attributionGateShown);
}

export function trackAttributionGateSignedOut(): void {
	capture(ACTIVATION_EVENTS.attributionGateSignedOut);
}

export function trackOnboardingOverviewShown(): void {
	capture(ACTIVATION_EVENTS.onboardingOverviewShown);
}

export function trackOnboardingOverviewSkipped(): void {
	capture(ACTIVATION_EVENTS.onboardingOverviewSkipped);
}

export function trackOnboardingOverviewCompleted(): void {
	capture(ACTIVATION_EVENTS.onboardingOverviewCompleted);
}

export function trackVerifyEmailGateShown(): void {
	capture(ACTIVATION_EVENTS.verifyEmailGateShown);
}

export function trackVerifyEmailResendClicked(): void {
	capture(ACTIVATION_EVENTS.verifyEmailResendClicked);
}

/**
 * An Add Company wizard step was finished.
 *
 * 🪤 `step` is a stable name, never the index. The Download Plugin step is
 * conditional (`isPluginNeedInstall`), so index 2 is "Download Plugin" for one
 * user and "Done" for the next — an index-keyed funnel would silently compare
 * two different steps.
 */
export function trackAddCompanyStepCompleted(step: string): void {
	capture(ACTIVATION_EVENTS.addCompanyStepCompleted, { step });
}

/** The wizard was closed on `step` without reaching the end. */
export function trackAddCompanyAbandoned(step: string): void {
	capture(ACTIVATION_EVENTS.addCompanyAbandoned, { step });
}

/**
 * A trialing team reached for Add Company and the one-company trial limit
 * stopped it.
 *
 * Autocapture cannot answer this one: the disabled trigger and the working
 * button both read as "Add Company", and the class attribute that would
 * separate them is redacted in `elements_chain`.
 *
 * 🪤 Only the two values the restriction was decided from travel. The team
 * and the company both have names and the user has an email; none of them size
 * the friction, so none of them are attached.
 */
export function trackTrialCompanyLimitBlocked(
	trigger: TrialLimitBlockedTrigger,
	teamCompanyCount: number | undefined,
	subscriptionStatus: string | undefined,
): void {
	capture(ACTIVATION_EVENTS.trialCompanyLimitBlocked, {
		trigger,
		teamCompanyCount,
		subscriptionStatus,
	});
}

// `recon_wizard_opened` and `reconciliation_start` are NOT declared here. Both
// are emitted by `trackEvents.reconWizard.opened` / `trackEvents.reconciliation
// .start` in `utils/analytics.ts`, which now reaches PostHog through the
// `analytics.on("track")` mirror. A second emitter for either name would double
// the wizard funnel's denominator with nothing raising an error.
//
// The ANSWER to the attribution gate is not declared here either. It is
// `signup_attribution_submitted`, captured inside `submitAttribution`
// (`hooks/useOnboarding.ts`) — the only place that can tell a saved answer apart
// from a re-submit of one already recorded. This module declared
// `attribution_gate_submitted` and `utils/analytics.ts` declared
// `attribution_submitted`, so one click emitted three differently-named events
// and the gate's pass-through numerator read 3x (EQU-401). Do not re-add either
// name: `attribution_gate_shown` (above) is the denominator, and
// `signup_attribution_submitted` is the whole numerator.

/**
 * A user asked to convert to a paid subscription from /account/billing.
 *
 * 🪤 Fires on the CLICK, before the checkout session exists — not on arriving
 * at Stripe. The two are different questions and only the first one is
 * answerable here: `GET /user/subscription/checkout` can fail (no price id on
 * the plan, a Stripe outage, a 400 on a bad `source`), and a click that we
 * dropped on the floor is exactly the failure this event has to be able to
 * show. Capturing after the redirect would record intent and delivery as one
 * number and hide that gap.
 *
 * `checkoutSource` is the `source` forwarded to the API and written to the
 * Stripe session's `metadata.source`; it is the join key between this event and
 * the Stripe-side conversion. Only the trialing banner has one.
 */
export function trackTrialConvertClicked(
	source: TrialConvertSource,
	checkoutSource?: string,
): void {
	capture(ACTIVATION_EVENTS.trialConvertClicked, {
		source,
		...(checkoutSource ? { checkoutSource } : {}),
	});
}
